import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import {
  useAdminDocuments,
  useUpdateDocument,
  useCreateVersion,
  usePublishVersion,
  useDeleteVersion,
  useUpdateVersion,
  useCreateSection,
  useReorderSections,
  useCreateActivity,
  useReorderActivities,
  useCurrentAdmin,
} from "@/lib/queries";
import { api, ApiError } from "@/lib/api";
import { toast } from "sonner";
import { flushAllSections } from "@/lib/sectionSaveRegistry";
import { stripHtml } from "@/lib/utils";
import type {
  AdminVersion,
  AdminSection,
  AdminDocument,
  AdminWorkflowActivity,
  VersionLock,
} from "@/lib/types";
import { ComplianceTagEditor } from "@/components/admin/ComplianceTagEditor";
import { KeywordEditor } from "@/components/admin/KeywordEditor";
import { SectionEditor } from "@/components/admin/SectionEditor";
import { ActivityEditor } from "@/components/admin/ActivityEditor";
import { PublishModal } from "@/components/admin/PublishModal";
import { TiptapEditor } from "@/components/admin/TiptapEditor";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CitationsToggle } from "@/components/CitationsToggle";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useActiveSection } from "@/lib/useActiveSection";
import { hasPermission, PERMISSIONS } from "@/lib/permissions";
import {
  Plus,
  ArrowLeft,
  FileCheck,
  Pencil,
  Trash2,
  History,
  ChevronDown,
  ChevronRight,
  Loader2,
} from "lucide-react";

const filterRecentUpdates = <T extends { heading: string }>(
  sections: T[],
): T[] => sections.filter((s) => s.heading !== "Recent Updates");

const filterActivitiesRecentUpdates = (
  activities: AdminWorkflowActivity[],
): AdminWorkflowActivity[] =>
  activities.map((a) => ({ ...a, sections: filterRecentUpdates(a.sections) }));

const IDLE_WARNING_MS = 10 * 60 * 1000;
const IDLE_RELEASE_MS = 15 * 60 * 1000;

export function DocumentEditorPage() {
  const { id } = useParams({ strict: false });
  const navigate = useNavigate();
  const documentId = parseInt(id as string);

  const { data: documents = [], refetch: refetchDocuments } =
    useAdminDocuments();
  const updateDocument = useUpdateDocument();
  const createVersion = useCreateVersion();
  const publishVersion = usePublishVersion();
  const deleteVersion = useDeleteVersion();
  const createSection = useCreateSection();
  const reorderSections = useReorderSections();
  const createActivity = useCreateActivity();
  const reorderActivities = useReorderActivities();

  const [document, setDocument] = useState<AdminDocument | null>(null);
  const [currentVersion, setCurrentVersion] = useState<AdminVersion | null>(
    null,
  );
  const [sections, setSections] = useState<AdminSection[]>([]);
  const [activities, setActivities] = useState<AdminWorkflowActivity[]>([]);
  const [publishedSections, setPublishedSections] = useState<AdminSection[]>(
    [],
  );
  const [publishedActivities, setPublishedActivities] = useState<
    AdminWorkflowActivity[]
  >([]);
  const [editableTitle, setEditableTitle] = useState("");
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [archivedVersions, setArchivedVersions] = useState<AdminVersion[]>([]);
  const [publishedVersion, setPublishedVersion] = useState<AdminVersion | null>(
    null,
  );
  const [editPublishedSummaryOpen, setEditPublishedSummaryOpen] =
    useState(false);
  const updateVersion = useUpdateVersion();
  const [activeTab, setActiveTab] = useState<string>("published");
  const [lock, setLock] = useState<VersionLock | null>(null);
  const [idleWarningOpen, setIdleWarningOpen] = useState(false);
  const holdsLock = lock?.mine === true;

  const { data: currentAdminData } = useCurrentAdmin();
  const canEditDocs = hasPermission(currentAdminData, PERMISSIONS.EDIT_DOCS);
  const canPublishDocs = hasPermission(
    currentAdminData,
    PERMISSIONS.PUBLISH_DOCS,
  );

  // Tracks whether the previous lock response said we held the lock, so the
  // heartbeat can announce the moment someone takes the lock away from us.
  const hadLockRef = useRef(false);
  // Idle release: after this long without user interaction, kick the editor
  // back to the content page so a waiting colleague isn't blocked by an
  // abandoned tab. Unmounting releases the lock via the effect cleanup.
  const lastActivityRef = useRef(Date.now());
  const documentTitleRef = useRef("");
  documentTitleRef.current = document?.title ?? "";

  const applyLockState = useCallback((next: VersionLock) => {
    if (hadLockRef.current && !next.mine && next.locked_by) {
      toast.warning(
        `${next.locked_by.email} took over editing this document. Your unsaved changes are still on screen — copy anything you need.`,
        { duration: 10000 },
      );
    }
    hadLockRef.current = next.mine;
    setLock(next);
  }, []);

  const claimLock = useCallback(
    async (versionId: number, force = false) => {
      try {
        const result = await api.post<VersionLock>(
          `/api/v1/admin/versions/${versionId}/lock`,
          force ? { force: true } : undefined,
        );
        applyLockState(result);
      } catch (error) {
        if (error instanceof ApiError && error.status === 423) {
          applyLockState(error.data as VersionLock);
        } else {
          console.error("Failed to claim editing lock:", error);
        }
      }
    },
    [applyLockState],
  );

  // Claim the editing lock while a draft is open; heartbeat keeps it fresh.
  // A demoted editor's heartbeat keeps polling and re-claims automatically
  // once the other admin's lock is released or expires.
  useEffect(() => {
    if (!currentVersion || currentVersion.status !== "draft" || !canEditDocs) {
      hadLockRef.current = false;
      setLock(null);
      return;
    }

    const versionId = currentVersion.id;
    lastActivityRef.current = Date.now();
    claimLock(versionId);

    const release = () => {
      // window.document: this component shadows `document` with state
      const csrf = window.document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");
      fetch(`/api/v1/admin/versions/${versionId}/lock`, {
        method: "DELETE",
        keepalive: true,
        credentials: "same-origin",
        headers: csrf ? { "X-CSRF-Token": csrf } : {},
      }).catch(() => {});
    };

    // Any interaction anywhere (including inside the idle-warning dialog)
    // counts as activity and dismisses the warning.
    const markActivity = () => {
      lastActivityRef.current = Date.now();
      setIdleWarningOpen(false);
    };
    const activityEvents = [
      "pointerdown",
      "keydown",
      "wheel",
      "touchstart",
    ] as const;
    activityEvents.forEach((e) =>
      window.addEventListener(e, markActivity, { passive: true }),
    );

    const tick = () => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs > IDLE_RELEASE_MS) {
        // Navigating away unmounts this effect; its cleanup releases the lock.
        toast.warning(
          `You are no longer editing the "${documentTitleRef.current}" due to inactivity.`,
          { duration: 10000 },
        );
        navigate({ to: "/admin/content" });
        return;
      }
      setIdleWarningOpen(idleMs > IDLE_WARNING_MS);
      claimLock(versionId);
    };
    const interval = setInterval(tick, 10_000);

    window.addEventListener("beforeunload", release);

    return () => {
      clearInterval(interval);
      activityEvents.forEach((e) => window.removeEventListener(e, markActivity));
      window.removeEventListener("beforeunload", release);
      release();
      hadLockRef.current = false;
      setLock(null);
      setIdleWarningOpen(false);
    };
  }, [currentVersion?.id, currentVersion?.status, canEditDocs, claimLock, navigate]);

  useEffect(() => {
    const doc = documents.find((d) => d.id === documentId);
    if (doc) {
      setDocument(doc);
      setEditableTitle(doc.title);
      loadVersions(doc);
    }
  }, [documents, documentId]);

  const loadVersions = async (doc: AdminDocument) => {
    setIsLoading(true);
    try {
      const versions = await api.get<AdminVersion[]>(
        `/api/v1/admin/documents/${doc.id}/versions`,
      );

      const archived = versions
        .filter((v) => v.status === "archived")
        .sort((a, b) => {
          const aDate = a.archived_at || a.updated_at;
          const bDate = b.archived_at || b.updated_at;
          return new Date(bDate).getTime() - new Date(aDate).getTime();
        });
      setArchivedVersions(archived);

      if (doc.has_published) {
        const published = versions.find((v) => v.status === "published");
        if (published) {
          setPublishedVersion(published);
          if (published.activities && published.activities.length > 0) {
            // Load full activity data with sections
            const fullActivities = await api.get<AdminWorkflowActivity[]>(
              `/api/v1/admin/versions/${published.id}/activities`,
            );
            setPublishedActivities(
              filterActivitiesRecentUpdates(fullActivities),
            );
            setPublishedSections([]);
          } else {
            const pubSections = await api.get<AdminSection[]>(
              `/api/v1/admin/versions/${published.id}/sections`,
            );
            setPublishedSections(filterRecentUpdates(pubSections));
            setPublishedActivities([]);
          }
        }
      }

      if (doc.has_draft) {
        const draft = versions.find((v) => v.status === "draft");
        if (draft) {
          setCurrentVersion(draft);
          if (draft.activities && draft.activities.length > 0) {
            const fullActivities = await api.get<AdminWorkflowActivity[]>(
              `/api/v1/admin/versions/${draft.id}/activities`,
            );
            setActivities(filterActivitiesRecentUpdates(fullActivities));
            setSections([]);
          } else {
            const draftSections = await api.get<AdminSection[]>(
              `/api/v1/admin/versions/${draft.id}/sections`,
            );
            setSections(filterRecentUpdates(draftSections));
            setActivities([]);
          }
        }
        setActiveTab("draft");
      } else if (doc.has_published) {
        setActiveTab("published");
      }
    } catch (error) {
      console.error("Failed to load versions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateDraft = async () => {
    try {
      const newVersion = await createVersion.mutateAsync(documentId);
      setCurrentVersion(newVersion);

      // Optimistically update local document state so UI reflects draft immediately
      if (document) {
        setDocument({ ...document, has_draft: true });
      }

      if (newVersion.activities && newVersion.activities.length > 0) {
        const fullActivities = await api.get<AdminWorkflowActivity[]>(
          `/api/v1/admin/versions/${newVersion.id}/activities`,
        );
        setActivities(filterActivitiesRecentUpdates(fullActivities));
        setSections([]);
      } else {
        const draftSections = await api.get<AdminSection[]>(
          `/api/v1/admin/versions/${newVersion.id}/sections`,
        );
        setSections(filterRecentUpdates(draftSections));
        setActivities([]);
      }

      setActiveTab("draft");
      await refetchDocuments();
    } catch (error) {
      console.error("Failed to create draft:", error);
    }
  };

  const handleTitleSave = async () => {
    if (editableTitle.trim() === "" || editableTitle === document?.title) {
      setEditableTitle(document?.title || "");
      setIsTitleEditing(false);
      return;
    }

    try {
      await updateDocument.mutateAsync({
        id: documentId,
        title: editableTitle,
      });
      setIsTitleEditing(false);
    } catch (error) {
      console.error("Failed to update title:", error);
      setEditableTitle(document?.title || "");
    }
  };

  const handlePublish = async (
    changeSummary: string,
    publishedAt: string,
    adminOnly: boolean = false,
  ) => {
    if (!currentVersion) return;

    try {
      await flushAllSections();
      await publishVersion.mutateAsync({
        id: currentVersion.id,
        change_summary: changeSummary || undefined,
        published_at: publishedAt || undefined,
        admin_only: adminOnly,
      });
      setPublishModalOpen(false);
      setCurrentVersion(null);
      setSections([]);
      setActivities([]);
      await refetchDocuments();
    } catch (error) {
      console.error("Failed to publish version:", error);
    }
  };

  const handleDeleteDraft = async () => {
    if (!currentVersion) return;

    try {
      await deleteVersion.mutateAsync(currentVersion.id);
      setDeleteConfirmOpen(false);
      setCurrentVersion(null);
      setSections([]);
      setActivities([]);
      setActiveTab("published");
      await refetchDocuments();
    } catch (error) {
      console.error("Failed to delete draft:", error);
    }
  };

  const handleAddSection = async () => {
    if (!currentVersion) return;

    try {
      const newSection = await createSection.mutateAsync({
        versionId: currentVersion.id,
        heading: "New Section",
        body_html: "",
      });

      setSections([...sections, newSection]);

      const updatedVersion = {
        ...currentVersion,
        sections: [
          ...currentVersion.sections,
          {
            id: newSection.id,
            section_key: newSection.section_key,
            heading: newSection.heading,
            position: newSection.position,
          },
        ],
      };
      setCurrentVersion(updatedVersion);
    } catch (error) {
      console.error("Failed to add section:", error);
    }
  };

  const handleSectionUpdate = (update: {
    id: number;
    heading: string;
    body_html: string;
    lock_version: number;
  }) => {
    setSections(
      sections.map((s) => (s.id === update.id ? { ...s, ...update } : s)),
    );
  };

  const handleSectionDelete = (sectionId: number) => {
    setSections(sections.filter((s) => s.id !== sectionId));
    if (currentVersion) {
      setCurrentVersion({
        ...currentVersion,
        sections: currentVersion.sections.filter((s) => s.id !== sectionId),
      });
    }
  };

  const handleMoveSection = async (index: number, direction: "up" | "down") => {
    if (!currentVersion) return;

    const newSections = [...sections];
    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newSections.length) return;
    [newSections[index], newSections[targetIndex]] = [
      newSections[targetIndex],
      newSections[index],
    ];

    const newOrder = newSections.map((s) => s.id);

    try {
      const reordered = await reorderSections.mutateAsync({
        versionId: currentVersion.id,
        section_ids: newOrder,
      });
      setSections(reordered);
    } catch (error) {
      console.error("Failed to reorder sections:", error);
    }
  };

  const handleAddActivity = async () => {
    if (!currentVersion) return;

    try {
      const newActivity = await createActivity.mutateAsync({
        versionId: currentVersion.id,
        name: "New Activity",
      });
      setActivities([...activities, newActivity]);
    } catch (error) {
      console.error("Failed to add activity:", error);
    }
  };

  const handleActivityUpdate = (updated: AdminWorkflowActivity) => {
    setActivities(activities.map((a) => (a.id === updated.id ? updated : a)));
  };

  const handleActivityDelete = (activityId: number) => {
    setActivities(activities.filter((a) => a.id !== activityId));
  };

  const handleMoveActivity = async (
    index: number,
    direction: "up" | "down",
  ) => {
    if (!currentVersion) return;

    const newActivities = [...activities];
    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newActivities.length) return;
    [newActivities[index], newActivities[targetIndex]] = [
      newActivities[targetIndex],
      newActivities[index],
    ];

    const newOrder = newActivities.map((a) => a.id);

    try {
      const reordered = await reorderActivities.mutateAsync({
        versionId: currentVersion.id,
        activity_ids: newOrder,
      });
      setActivities(reordered);
    } catch (error) {
      console.error("Failed to reorder activities:", error);
    }
  };

  if (!document) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center text-muted-foreground">
          Loading document...
        </div>
      </div>
    );
  }

  const isWorkflow = document.document_type === "workflow_template";
  const hasContent = sections.length > 0 || activities.length > 0;
  const canPublish = document.has_draft && currentVersion && hasContent;

  return (
    <div className="container mx-auto py-8 max-w-5xl">
      <Button
        variant="ghost"
        onClick={() => navigate({ to: "/admin/content" })}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Documents
      </Button>

      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            {canEditDocs && isTitleEditing ? (
              <Input
                value={editableTitle}
                onChange={(e) => setEditableTitle(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleTitleSave();
                  } else if (e.key === "Escape") {
                    setEditableTitle(document.title);
                    setIsTitleEditing(false);
                  }
                }}
                autoFocus
                className="text-2xl font-bold h-auto py-2"
              />
            ) : (
              <h1
                className={`text-2xl font-bold ${canEditDocs ? "cursor-pointer hover:text-muted-foreground transition-colors" : ""}`}
                onClick={() => canEditDocs && setIsTitleEditing(true)}
              >
                {document.title}
              </h1>
            )}
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <span>
                {document.document_type === "clinical_review"
                  ? "Clinical Review"
                  : document.document_type === "workflow_template"
                    ? "Workflow"
                    : document.document_type === "static_page"
                      ? "Static Page"
                      : "Supplemental"}
              </span>
              {document.has_published && (
                <span className="text-lime-600 font-medium">Published</span>
              )}
              {document.has_draft && <span>Draft</span>}
            </div>
            {canEditDocs && document.document_type !== "clinical_review" && (
              <ComplianceTagEditor
                tags={document.compliance_tags}
                onChange={async (tags) => {
                  try {
                    await updateDocument.mutateAsync({
                      id: documentId,
                      compliance_tags: tags,
                    });
                  } catch (error) {
                    console.error("Failed to update compliance tags:", error);
                  }
                }}
              />
            )}
            {canEditDocs && (
              <KeywordEditor
                keywords={document.keywords}
                onChange={async (keywords) => {
                  try {
                    await updateDocument.mutateAsync({
                      id: documentId,
                      keywords,
                    });
                  } catch (error) {
                    console.error("Failed to update keywords:", error);
                  }
                }}
              />
            )}
            {canEditDocs && document.document_type === "static_page" && (
              <div className="flex items-center gap-2 mt-3">
                <Checkbox
                  id="show-in-footer"
                  checked={document.show_in_footer}
                  onCheckedChange={async (checked) => {
                    try {
                      await updateDocument.mutateAsync({
                        id: documentId,
                        show_in_footer: !!checked,
                      });
                    } catch (error) {
                      console.error("Failed to update show_in_footer:", error);
                    }
                  }}
                />
                <Label
                  htmlFor="show-in-footer"
                  className="text-sm cursor-pointer"
                >
                  Show in footer
                </Label>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {canEditDocs && !document.has_draft && document.has_published && (
              <Button onClick={handleCreateDraft}>
                <Pencil className="h-4 w-4 mr-2" />
                Create Draft
              </Button>
            )}
            {document.has_draft && (
              <>
                {canEditDocs && (
                  <Button
                    variant="destructive"
                    onClick={() => setDeleteConfirmOpen(true)}
                    disabled={!holdsLock}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Draft
                  </Button>
                )}
                {canPublishDocs && (
                  <Button
                    onClick={() => setPublishModalOpen(true)}
                    disabled={!canPublish || !holdsLock}
                  >
                    <FileCheck className="h-4 w-4 mr-2" />
                    Publish
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        <Separator />

        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground">
            Loading...
          </div>
        ) : !document.has_draft && !document.has_published ? (
          <div className="text-center py-12 space-y-4">
            <p className="text-muted-foreground">
              This document has no versions yet.
            </p>
            {canEditDocs && (
              <Button onClick={handleCreateDraft}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Draft
              </Button>
            )}
          </div>
        ) : (
          (() => {
            const tabCount =
              (document.has_draft ? 1 : 0) +
              (document.has_published ? 1 : 0) +
              (archivedVersions.length > 0 ? 1 : 0);

            const lockBanner =
              lock && !lock.mine && lock.locked_by ? (
                <div className="flex items-center justify-between gap-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-700 dark:bg-amber-950">
                  <span>
                    {lock.locked_by.email} is editing this document
                    {lock.locked_at &&
                      ` (since ${new Date(lock.locked_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })})`}
                    . The draft is read-only they finish their changes, or you
                    take over.
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      currentVersion && claimLock(currentVersion.id, true)
                    }
                  >
                    Take over
                  </Button>
                </div>
              ) : null;

            const draftContent = (
              <>
                {lockBanner}
                {activities.length > 0 ? (
                  <WorkflowDraftEditor
                    activities={activities}
                    onMoveActivity={handleMoveActivity}
                    onUpdateActivity={handleActivityUpdate}
                    onDeleteActivity={handleActivityDelete}
                    onAddActivity={handleAddActivity}
                    canEdit={canEditDocs && holdsLock}
                  />
                ) : (
                  <DraftEditor
                    sections={sections}
                    isWorkflow={isWorkflow}
                    showCitationButton={
                      document.document_type !== "clinical_review"
                    }
                    onMoveSection={handleMoveSection}
                    onUpdate={handleSectionUpdate}
                    onDelete={handleSectionDelete}
                    onAddSection={handleAddSection}
                    canEdit={canEditDocs && holdsLock}
                  />
                )}
              </>
            );

            const publishedContent =
              publishedActivities.length > 0 ? (
                <PublishedActivityPreview
                  activities={publishedActivities}
                  showCitationsToggle={
                    document.document_type !== "clinical_review"
                  }
                />
              ) : (
                <PublishedPreview
                  sections={publishedSections}
                  showCitationsToggle={
                    document.document_type !== "clinical_review"
                  }
                />
              );

            if (tabCount <= 1) {
              return document.has_draft ? (
                <div className="space-y-6">{draftContent}</div>
              ) : (
                <div className="space-y-4">{publishedContent}</div>
              );
            }

            return (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                  {document.has_draft && (
                    <TabsTrigger value="draft">Draft</TabsTrigger>
                  )}
                  {document.has_published && (
                    <TabsTrigger value="published">Published</TabsTrigger>
                  )}
                  {archivedVersions.length > 0 && (
                    <TabsTrigger value="history">
                      <History className="h-4 w-4 mr-1.5" />
                      History
                    </TabsTrigger>
                  )}
                </TabsList>
                {document.has_draft && (
                  <TabsContent value="draft" className="space-y-6 mt-4">
                    {draftContent}
                  </TabsContent>
                )}
                {document.has_published && (
                  <TabsContent value="published" className="space-y-4 mt-4">
                    {publishedVersion && (
                      <div className="flex items-start gap-2 rounded-lg border bg-muted/30 px-4 py-3">
                        <div className="flex-1 min-w-0 space-y-2">
                          {publishedVersion.change_summary && (
                            <div
                              className="prose prose-sm max-w-none text-sm text-muted-foreground"
                              dangerouslySetInnerHTML={{
                                __html: publishedVersion.change_summary,
                              }}
                            />
                          )}
                          {publishedVersion.published_at && (
                            <p className="text-sm text-muted-foreground">
                              Published:{" "}
                              {new Date(
                                publishedVersion.published_at,
                              ).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        {canEditDocs && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            onClick={() => setEditPublishedSummaryOpen(true)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    )}
                    {publishedContent}
                  </TabsContent>
                )}
                {archivedVersions.length > 0 && (
                  <TabsContent value="history" className="space-y-3 mt-4">
                    <p className="text-sm text-muted-foreground">
                      {archivedVersions.length} previous{" "}
                      {archivedVersions.length === 1 ? "version" : "versions"}
                    </p>
                    {archivedVersions.map((version) => (
                      <ArchivedVersionEntry
                        key={version.id}
                        version={version}
                        isWorkflow={isWorkflow}
                        showCitationsToggle={
                          document.document_type !== "clinical_review"
                        }
                        canEditDocs={canEditDocs}
                      />
                    ))}
                  </TabsContent>
                )}
              </Tabs>
            );
          })()
        )}
      </div>

      <PublishModal
        open={publishModalOpen}
        onOpenChange={setPublishModalOpen}
        onPublish={handlePublish}
        isPublishing={publishVersion.isPending}
      />

      {canEditDocs && publishedVersion && (
        <EditSummaryModal
          open={editPublishedSummaryOpen}
          onOpenChange={setEditPublishedSummaryOpen}
          initialSummary={publishedVersion.change_summary || ""}
          initialPublishedAt={publishedVersion.published_at}
          onSave={async (summary, publishedAt) => {
            await updateVersion.mutateAsync({
              id: publishedVersion.id,
              change_summary: summary,
              ...(publishedAt && { published_at: publishedAt }),
            });
            setPublishedVersion({
              ...publishedVersion,
              change_summary: summary,
              ...(publishedAt && { published_at: publishedAt }),
            });
          }}
        />
      )}

      {canEditDocs && (
        <AlertDialog
          open={deleteConfirmOpen}
          onOpenChange={setDeleteConfirmOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Draft</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the current draft and all its
                changes.
                {document.has_published
                  ? " The published version will not be affected."
                  : " This document will have no versions."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteDraft}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={deleteVersion.isPending}
              >
                {deleteVersion.isPending ? "Deleting..." : "Delete Draft"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <AlertDialog open={idleWarningOpen} onOpenChange={setIdleWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you still editing?</AlertDialogTitle>
            <AlertDialogDescription>
              You have been inactive for 10 minutes. You will stop editing
              &quot;{document.title}&quot; and be returned to the content page
              after 15 minutes of inactivity.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Keep Editing</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function WorkflowDraftEditor({
  activities,
  onMoveActivity,
  onUpdateActivity,
  onDeleteActivity,
  onAddActivity,
  canEdit = true,
}: {
  activities: AdminWorkflowActivity[];
  onMoveActivity: (index: number, direction: "up" | "down") => void;
  onUpdateActivity: (activity: AdminWorkflowActivity) => void;
  onDeleteActivity: (activityId: number) => void;
  onAddActivity: () => void;
  canEdit?: boolean;
}) {
  const allSectionKeys = activities.flatMap((a) =>
    a.sections.map((s) => s.section_key),
  );
  const activeSection = useActiveSection(allSectionKeys);

  return (
    <div className="flex gap-6">
      <div className="min-w-0 flex-1 space-y-6">
        <div className="space-y-6">
          {activities.map((activity, index) => (
            <ActivityEditor
              key={activity.id}
              activity={activity}
              isFirst={index === 0}
              isLast={index === activities.length - 1}
              onMoveUp={() => onMoveActivity(index, "up")}
              onMoveDown={() => onMoveActivity(index, "down")}
              onUpdate={onUpdateActivity}
              onDelete={() => onDeleteActivity(activity.id)}
              canEdit={canEdit}
            />
          ))}
        </div>

        {canEdit && (
          <Button variant="outline" onClick={onAddActivity} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Activity
          </Button>
        )}
      </div>

      <aside className="hidden w-48 shrink-0 lg:block">
        <div className="sticky top-0 pt-6">
          <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
            Contents
          </h3>
          <ScrollArea className="h-[calc(100vh-16rem)]">
            <nav className="space-y-2">
              {activities.map((activity) => (
                <div key={activity.id}>
                  <span className="block px-2 py-1 text-sm font-medium text-foreground">
                    {activity.name}
                  </span>
                  <div className="ml-2 space-y-0.5">
                    {activity.sections.map((section) => (
                      <a
                        key={section.id}
                        href={`#section-${section.section_key}`}
                        className={`block rounded-md px-2 py-0.5 text-xs transition-colors ${
                          activeSection === section.section_key
                            ? "bg-accent text-foreground font-medium"
                            : "text-muted-foreground hover:bg-accent hover:text-foreground"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          document
                            .getElementById(`section-${section.section_key}`)
                            ?.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        {section.heading}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </ScrollArea>
        </div>
      </aside>
    </div>
  );
}

function DraftEditor({
  sections,
  isWorkflow,
  showCitationButton,
  onMoveSection,
  onUpdate,
  onDelete,
  onAddSection,
  canEdit = true,
}: {
  sections: AdminSection[];
  isWorkflow: boolean;
  showCitationButton?: boolean;
  onMoveSection: (index: number, direction: "up" | "down") => void;
  onUpdate: (update: {
    id: number;
    heading: string;
    body_html: string;
    lock_version: number;
  }) => void;
  onDelete: (sectionId: number) => void;
  onAddSection: () => void;
  canEdit?: boolean;
}) {
  const activeSection = useActiveSection(sections.map((s) => s.section_key));

  return (
    <div className="flex gap-6">
      <div className="min-w-0 flex-1 space-y-4">
        <div className="space-y-4">
          {sections.map((section, index) => (
            <SectionEditor
              key={section.id}
              section={section}
              sectionKey={section.section_key}
              isWorkflow={isWorkflow}
              showCitationButton={showCitationButton}
              isFirst={index === 0}
              isLast={index === sections.length - 1}
              onMoveUp={() => onMoveSection(index, "up")}
              onMoveDown={() => onMoveSection(index, "down")}
              onUpdate={onUpdate}
              onDelete={() => onDelete(section.id)}
              canEdit={canEdit}
            />
          ))}
        </div>

        {canEdit && (
          <Button variant="outline" onClick={onAddSection} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Section
          </Button>
        )}
      </div>

      <aside className="hidden w-48 shrink-0 lg:block">
        <div className="sticky top-0 pt-6">
          <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
            Contents
          </h3>
          <ScrollArea className="h-[calc(100vh-16rem)]">
            <nav className="space-y-1">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#section-${section.section_key}`}
                  className={`block rounded-md px-2 py-1 text-sm transition-colors ${
                    activeSection === section.section_key
                      ? "bg-accent text-foreground font-medium"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById(`section-${section.section_key}`)
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  {section.heading}
                </a>
              ))}
            </nav>
          </ScrollArea>
        </div>
      </aside>
    </div>
  );
}

function ArchivedVersionEntry({
  version,
  isWorkflow,
  showCitationsToggle = true,
  canEditDocs,
}: {
  version: AdminVersion;
  isWorkflow: boolean;
  showCitationsToggle?: boolean;
  canEditDocs: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState<AdminSection[] | null>(null);
  const [activities, setActivities] = useState<AdminWorkflowActivity[] | null>(
    null,
  );
  const [editSummaryOpen, setEditSummaryOpen] = useState(false);
  const [currentSummary, setCurrentSummary] = useState(
    version.change_summary || "",
  );
  const updateVersion = useUpdateVersion();

  const handleToggle = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }

    setExpanded(true);

    if (sections !== null || activities !== null) return;

    setLoading(true);
    try {
      if (version.activities && version.activities.length > 0) {
        const data = await api.get<AdminWorkflowActivity[]>(
          `/api/v1/admin/versions/${version.id}/activities`,
        );
        setActivities(filterActivitiesRecentUpdates(data));
      } else {
        const data = await api.get<AdminSection[]>(
          `/api/v1/admin/versions/${version.id}/sections`,
        );
        setSections(filterRecentUpdates(data));
      }
    } catch (error) {
      console.error("Failed to load archived version content:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSummary = async (summary: string) => {
    await updateVersion.mutateAsync({
      id: version.id,
      change_summary: summary,
    });
    setCurrentSummary(summary);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString("en-CA", { timeZone: "UTC" });
  };

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center gap-1 pr-2">
        <button
          onClick={handleToggle}
          className="flex flex-1 items-center gap-3 px-4 py-3 text-left hover:bg-accent/50 transition-colors rounded-lg min-w-0"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0 flex-1">
            <p className="font-medium truncate">
              {currentSummary ? stripHtml(currentSummary) : "No summary"}
            </p>
            <div className="flex gap-4 text-xs text-muted-foreground mt-0.5">
              {version.published_at && (
                <span>Published {formatDate(version.published_at)}</span>
              )}
              {version.archived_at && (
                <span>Archived {formatDate(version.archived_at)}</span>
              )}
            </div>
          </div>
        </button>
        {canEditDocs && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              setEditSummaryOpen(true);
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {canEditDocs && (
        <EditSummaryModal
          open={editSummaryOpen}
          onOpenChange={setEditSummaryOpen}
          initialSummary={currentSummary}
          onSave={handleSaveSummary}
        />
      )}

      {expanded && (
        <div className="border-t px-4 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Loading content...
            </div>
          ) : activities && activities.length > 0 ? (
            <PublishedActivityPreview
              activities={activities}
              showCitationsToggle={showCitationsToggle}
            />
          ) : sections && sections.length > 0 ? (
            <PublishedPreview
              sections={sections}
              showCitationsToggle={showCitationsToggle}
            />
          ) : (
            <p className="text-center py-8 text-muted-foreground">
              No content in this version.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function PublishedActivityPreview({
  activities,
  showCitationsToggle = true,
}: {
  activities: AdminWorkflowActivity[];
  showCitationsToggle?: boolean;
}) {
  const [showCitations, setShowCitations] = useState(false);

  const allSections = activities.flatMap((a) =>
    a.sections.map((s) => ({ ...s, activityName: a.name })),
  );
  const activeSection = useActiveSection(allSections.map((s) => s.section_key));

  if (activities.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No activities in published version.
      </div>
    );
  }

  return (
    <div className={`flex gap-6 ${showCitations ? "show-citations" : ""}`}>
      <div className="min-w-0 flex-1">
        <div className="flex justify-end mb-4">
          {showCitationsToggle && (
            <CitationsToggle
              enabled={showCitations}
              onChange={setShowCitations}
            />
          )}
        </div>
        <div className="space-y-6">
          {activities.map((activity) => (
            <div key={activity.id} className="space-y-4">
              <h2 className="text-xl font-semibold border-l-4 border-l-primary bg-muted/50 px-4 py-2 rounded-r-md">
                {activity.name}
              </h2>
              {activity.sections.map((section) => (
                <CollapsibleSection key={section.id} section={section} />
              ))}
            </div>
          ))}
        </div>
      </div>

      <aside className="hidden w-48 shrink-0 lg:block">
        <div className="sticky top-0">
          <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
            Contents
          </h3>
          <ScrollArea className="h-[calc(100vh-16rem)]">
            <nav className="space-y-2">
              {activities.map((activity) => (
                <div key={activity.id}>
                  <span className="block px-2 py-1 text-sm font-medium text-foreground">
                    {activity.name}
                  </span>
                  <div className="ml-2 space-y-0.5">
                    {activity.sections.map((section) => (
                      <a
                        key={section.id}
                        href={`#section-${section.section_key}`}
                        className={`block rounded-md px-2 py-0.5 text-xs transition-colors ${
                          activeSection === section.section_key
                            ? "bg-accent text-foreground font-medium"
                            : "text-muted-foreground hover:bg-accent hover:text-foreground"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          document
                            .getElementById(`section-${section.section_key}`)
                            ?.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        {section.heading}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </ScrollArea>
        </div>
      </aside>
    </div>
  );
}

function PublishedPreview({
  sections,
  showCitationsToggle = true,
}: {
  sections: AdminSection[];
  showCitationsToggle?: boolean;
}) {
  const [showCitations, setShowCitations] = useState(false);
  const activeSection = useActiveSection(sections.map((s) => s.section_key));

  if (sections.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No sections in published version.
      </div>
    );
  }

  return (
    <div className={`flex gap-6 ${showCitations ? "show-citations" : ""}`}>
      <div className="min-w-0 flex-1">
        <div className="flex justify-end mb-4">
          {showCitationsToggle && (
            <CitationsToggle
              enabled={showCitations}
              onChange={setShowCitations}
            />
          )}
        </div>
        <div className="space-y-4">
          {sections.map((section) => (
            <CollapsibleSection key={section.id} section={section} />
          ))}
        </div>
      </div>

      <aside className="hidden w-48 shrink-0 lg:block">
        <div className="sticky top-0">
          <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
            Contents
          </h3>
          <ScrollArea className="h-[calc(100vh-16rem)]">
            <nav className="space-y-1">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#section-${section.section_key}`}
                  className={`block rounded-md px-2 py-1 text-sm transition-colors ${
                    activeSection === section.section_key
                      ? "bg-accent text-foreground font-medium"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById(`section-${section.section_key}`)
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  {section.heading}
                </a>
              ))}
            </nav>
          </ScrollArea>
        </div>
      </aside>
    </div>
  );
}

function EditSummaryModal({
  open,
  onOpenChange,
  initialSummary,
  initialPublishedAt,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSummary: string;
  initialPublishedAt?: string | null;
  onSave: (summary: string, publishedAt?: string) => Promise<void>;
}) {
  const [summary, setSummary] = useState(initialSummary);
  const [noPublicChanges, setNoPublicChanges] = useState(false);
  const [publishedAt, setPublishedAt] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSummary(initialSummary);
      setNoPublicChanges(!initialSummary);
      setPublishedAt(
        initialPublishedAt
          ? new Date(initialPublishedAt).toISOString().split("T")[0]
          : "",
      );
    }
  }, [open, initialSummary, initialPublishedAt]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(noPublicChanges ? "" : summary, publishedAt || undefined);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Change Summary</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="edit-no-public-changes"
              checked={noPublicChanges}
              onCheckedChange={(checked) =>
                setNoPublicChanges(checked === true)
              }
            />
            <Label
              htmlFor="edit-no-public-changes"
              className="font-normal cursor-pointer"
            >
              No public changes
            </Label>
          </div>
          {!noPublicChanges && (
            <TiptapEditor
              content={summary}
              onChange={setSummary}
              showCitationButton={false}
            />
          )}
          {initialPublishedAt !== undefined && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Published date:
              </span>
              <Input
                type="date"
                max={new Date().toISOString().split("T")[0]}
                value={publishedAt}
                onChange={(e) => setPublishedAt(e.target.value)}
                className="h-8 w-44 text-sm"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
